/**
 * Test utilities for conditional data-testid rendering
 * This utility ensures data-testid attributes are only included in development/test environments
 * to minimize production bundle size.
 */

/**
 * Conditionally adds data-testid attribute based on environment
 * @param {string} testId - The test identifier
 * @returns {object} - Props object with data-testid if appropriate
 */
export const getTestId = (testId) => {
  // Only include data-testid in development, test, or when explicitly enabled
  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'test' ||
    process.env.REACT_APP_INCLUDE_TEST_IDS === 'true'
  ) {
    return { 'data-testid': testId };
  }
  return {};
};

/**
 * Hook for conditional test attributes
 * @param {string} testId - The test identifier
 * @returns {object} - Props object with data-testid if appropriate
 */
export const useTestId = (testId) => {
  return getTestId(testId);
};

/**
 * Higher-order component wrapper for adding conditional test IDs
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} testId - The test identifier
 * @returns {React.Component} - Wrapped component with conditional test ID
 */
export const withTestId = (WrappedComponent, testId) => {
  const ComponentWithTestId = (props) => {
    return <WrappedComponent {...getTestId(testId)} {...props} />;
  };
  
  ComponentWithTestId.displayName = `withTestId(${WrappedComponent.displayName || WrappedComponent.name})`;
  return ComponentWithTestId;
};

/**
 * Environment check utilities
 */
export const isTestEnvironment = () => {
  return (
    process.env.NODE_ENV === 'test' ||
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_INCLUDE_TEST_IDS === 'true'
  );
};

export const isProductionWithoutTestIds = () => {
  return (
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_INCLUDE_TEST_IDS !== 'true'
  );
};